import { useContext, useEffect } from "react";

import { useTranslation } from "react-i18next";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router";

import Snackbar from "../components/Snackbar/Snackbar";
import { useAuth } from "../context/Auth/AuthProvider";
import LanguageContext from "../context/language/LanguageContext";
import PasswordResetRequest from "../Pages/Auth/components/PasswordReset/PasswordResetRequest/PasswordResetRequest";
import ResetPassword from "../Pages/Auth/components/PasswordReset/ResetPassword/ResetPassword";
import CreateAccount from "../Pages/Auth/components/UserInvitation/CreateAccount/CreateAccount";
import Login from "../Pages/Auth/Login";
import Companies from "../Pages/Companies/Companies";
import Dashboard from "../Pages/Dashboard/Dashboard";
import Drivers from "../Pages/Drivers/Drivers";
import DriversFiles from "../Pages/Files/DriversFiles/DriversFiles";
import Files from "../Pages/Files/VehicleFiles/VehicleFiles";
import Integrations from "../Pages/Integrations/Integrations";
import IntegrationsApi from "../Pages/Integrations/IntegrationsApi/IntegrationsApi";
import Users from "../Pages/Users/Users";
import UsersInvitations from "../Pages/Users/UsersInvitations/UsersInvitations";
import Vehicles from "../Pages/Vehicles/Vehicles";
import { NAVIGATION_ITEMS, ROUTES } from "../shared";
import ErrorBoundary from "../shared/components/ErrorBoundary/ErrorBoundary";
import AuthLoader from "../shared/components/loaders/AuthLoader";
import { useReleaseFeatureToggle } from "../shared/featureToggles/hooks";
import { FEATURE_NAMES } from "../shared/featureToggles/types";

import "../shared/hooks/useSentry";

import AnalysisProviders from "@/Pages/Integrations/AnalysisProviders/AnalysisProviders";
import { USER_PERMISSIONS } from "@/roles/types/enums";
import { getAccessStatus } from "@/stores/useUserDataStore";

import GlobalStyles from "./GlobalStyles";
import { useHomeRoute } from "./hooks/useHomeRoute";
import PageWrapperRoute from "./PageWrapperRoute";
import ProtectedRoute from "./ProtectedRoute";
import RedirectHandler from "./RedirectHandler";
import RoutesContent from "./RoutesContent";

const Routes = () => {
  const { language } = useContext(LanguageContext);
  const { isAuthLoading, setIsAuthLoading, getUserData, tokenInfo, user } =
    useAuth();

  const canViewAnalysisProviders = getAccessStatus(
    USER_PERMISSIONS.SeeAnalysisProviders
  );

  const { isReleaseFeatureEnabledForRole } = useReleaseFeatureToggle();
  const { i18n } = useTranslation();

  const { canAccessDashboard } = useHomeRoute();

  useEffect(() => {
    initializeAuthState();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n, language, tokenInfo]);

  const initializeAuthState = () => {
    if (!tokenInfo) {
      i18n.changeLanguage(language);
      setIsAuthLoading(false);
    }

    if (tokenInfo) getUserData(true);
  };

  const isUserReady = !isAuthLoading && (!!user || !tokenInfo);
  if (!isUserReady) return <AuthLoader />;

  const canAccessIntegrations = isReleaseFeatureEnabledForRole(
    FEATURE_NAMES.ShowIntegrationsPage,
    user?.role
  );

  const navigateHome = () => {
    if (tokenInfo)
      return (
        <Navigate
          to={canAccessDashboard ? ROUTES.Dashboard : ROUTES.Vehicles}
          replace
        />
      );
    return <Navigate to={ROUTES.Home} replace />;
  };

  // Authenticated page config
  const pageRoutes = [
    {
      path: ROUTES.Dashboard,
      itemName: NAVIGATION_ITEMS.Dashboard,
      Component: Dashboard,
      enabled: canAccessDashboard
    },
    {
      path: ROUTES.Vehicles,
      itemName: NAVIGATION_ITEMS.Vehicles,
      Component: Vehicles,
      enabled: true
    },
    {
      path: ROUTES.Drivers,
      itemName: NAVIGATION_ITEMS.Drivers,
      Component: Drivers,
      enabled: true
    },
    {
      path: ROUTES.VehicleFiles,
      itemName: NAVIGATION_ITEMS.Files,
      Component: Files,
      enabled: true
    },
    {
      path: ROUTES.DriverFiles,
      itemName: NAVIGATION_ITEMS.Files,
      Component: DriversFiles,
      enabled: true
    },
    {
      path: ROUTES.Companies,
      itemName: NAVIGATION_ITEMS.Companies,
      Component: Companies,
      enabled: true
    },
    {
      path: ROUTES.Users,
      itemName: NAVIGATION_ITEMS.Users,
      Component: Users,
      enabled: true
    },
    {
      path: ROUTES.UsersInvitations,
      itemName: NAVIGATION_ITEMS.Users,
      Component: UsersInvitations,
      enabled: true
    },
    {
      path: ROUTES.Integrations,
      itemName: NAVIGATION_ITEMS.Integrations,
      Component: Integrations,
      enabled: canAccessIntegrations
    },
    {
      path: ROUTES.IntegrationsApi,
      itemName: NAVIGATION_ITEMS.Integrations,
      Component: IntegrationsApi,
      enabled: canAccessIntegrations
    },
    {
      path: ROUTES.AnalysisProviders,
      itemName: NAVIGATION_ITEMS.Integrations,
      Component: AnalysisProviders,
      enabled: canViewAnalysisProviders
    }
  ];

  const routesForAuthenticatedOnly = [
    {
      path: ROUTES.Home,
      element: <ProtectedRoute />,
      children: [
        { path: ROUTES.Home, element: navigateHome() },
        ...pageRoutes
          .filter(route => route.enabled)
          .map(route => ({
            path: route.path,
            element: (
              <PageWrapperRoute
                itemName={route.itemName}
                Component={route.Component}
              />
            )
          })),
        { path: ROUTES.NotFound, element: navigateHome() }
      ]
    }
  ];

  const routesForNotAuthenticatedOnly = [
    { path: ROUTES.Home, element: <Login /> },
    { path: ROUTES.RequestReset, element: <PasswordResetRequest /> },
    { path: ROUTES.ResetPassword, element: <ResetPassword /> },
    { path: ROUTES.UserInvitation, element: <CreateAccount /> }
  ];

  const router = createBrowserRouter([
    {
      element: (
        <ErrorBoundary>
          <RedirectHandler />
          <RoutesContent user={user} />
        </ErrorBoundary>
      ),
      children: [
        ...(tokenInfo ? [] : routesForNotAuthenticatedOnly),
        ...routesForAuthenticatedOnly
      ]
    }
  ]);

  return (
    <>
      <GlobalStyles />
      <Snackbar />
      <RouterProvider router={router} />
    </>
  );
};

export default Routes;
